const PUBLIC_PREFIX = '/uploads/avatars';

/** Safe public path served from `public/uploads/avatars/{users|agents}/{id}.ext` */
export function normalizeStoredAvatarUrl(url) {
  if (url == null) return '';
  let t = typeof url === 'string' ? url.trim() : String(url).trim();
  if (!t) return '';

  try {
    if (t.startsWith('http://') || t.startsWith('https://')) {
      t = new URL(t).pathname || '';
    }
  } catch {
    return '';
  }

  const q = t.indexOf('?');
  if (q !== -1) t = t.slice(0, q);
  const h = t.indexOf('#');
  if (h !== -1) t = t.slice(0, h);
  t = t.trim();

  if (t.includes('..') || !t.startsWith(PUBLIC_PREFIX)) return '';
  // Case-insensitive: ObjectId hex and file extension
  if (
    !/^\/uploads\/avatars\/(users|agents)\/[a-f0-9]{24}\.(jpeg|jpg|png|webp)$/i.test(t)
  ) {
    return '';
  }
  return t.replace(/\.jpg$/i, '.jpeg');
}

/** Tailwind classes for initials fallback (no uploaded image). */
export function panelAvatarFallbackBgClassName() {
  return 'bg-linear-to-br from-amber-600 to-orange-700';
}

export function publicAvatarPath(role, mongoId, extWithDot) {
  const sub = role === 'agent' ? 'agents' : 'users';
  const ext = extWithDot.toLowerCase() === '.jpg' ? '.jpeg' : extWithDot.toLowerCase();
  return `${PUBLIC_PREFIX}/${sub}/${mongoId}${ext}`;
}

export function extFromMimetype(mimetype) {
  if (!mimetype) return null;
  const m = String(mimetype).toLowerCase();
  if (m === 'image/jpeg' || m === 'image/jpg' || m === 'image/pjpeg') return '.jpeg';
  if (m === 'image/png' || m === 'image/x-png') return '.png';
  if (m === 'image/webp') return '.webp';
  return null;
}

/** When the client sends `application/octet-stream` or empty MIME, infer from filename. */
export function extFromFilename(name) {
  if (!name || typeof name !== 'string') return null;
  const m = name.toLowerCase().match(/\.(jpe?g|png|webp)$/i);
  if (!m) return null;
  const g = m[1].toLowerCase();
  if (g === 'jpeg' || g === 'jpg') return '.jpeg';
  if (g === 'png') return '.png';
  if (g === 'webp') return '.webp';
  return null;
}

/**
 * Image URL for `<img src>` — uses API so avatars work even when files are written at runtime
 * (Next dev / Turbopack may not serve new files under `public/` until restart).
 * @param {string} normalizedOrRaw from DB/session (`/uploads/avatars/users/{id}.jpeg`)
 * @param {number} [cacheVersion] bump after upload to bust browser cache
 */
export function avatarUrlToDisplaySrc(normalizedOrRaw, cacheVersion = 0) {
  const n = normalizeStoredAvatarUrl(normalizedOrRaw);
  if (!n) return '';
  const prefix = '/uploads/avatars/';
  if (!n.startsWith(prefix)) return '';
  const rel = n.slice(prefix.length);
  if (!rel || rel.includes('..')) return '';
  const segs = rel.split('/').filter(Boolean);
  if (segs.length !== 2) return '';
  const safePath = segs.map(encodeURIComponent).join('/');
  const v =
    typeof cacheVersion === 'number' && cacheVersion > 0 ? `?v=${cacheVersion}` : '';
  return `/api/serve-avatar/${safePath}${v}`;
}

/** Single letter fallback when no image */
export function avatarFallbackInitial(name, username, letter = '?') {
  const n = typeof name === 'string' ? name.trim() : '';
  const u = typeof username === 'string' ? username.trim() : '';
  return (n.charAt(0) || u.charAt(0) || letter).toUpperCase();
}
